import { useEffect, useMemo, useState } from 'react';
import { Stack, Group, Text, Button } from '@repletoryreach/sdk-ui';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../../i18n/I18nProvider';
import { NODE_KINDS, PALETTE_ORDER, outputsFor } from './catalog';
import { ICONS, portLabel, type Translate } from './nodes';
import { TRIGGER_NODE_TYPE } from '../mapper';

/**
 * "Add a step": the palette as a dialog, not a permanent column. The left
 * side lists every kind of step with a one-line hint; the right side explains
 * the highlighted one properly (what it does, when to use it, the ports it
 * leaves through) before it is added. Search filters the list.
 */
type Group = 'messages' | 'ask' | 'ai' | 'people' | 'logic' | 'connect';
const GROUP_OF: Record<string, Group> = {
  send: 'messages', template: 'messages', sms: 'messages', email: 'messages', ask: 'ask', form: 'ask', language: 'ask', booking: 'ask', manage_booking: 'ask', ticket: 'people', ticket_status: 'people', llm: 'ai', handoff: 'people', notify: 'people', contact: 'people',
  http: 'connect', lookup: 'connect', flow: 'logic', condition: 'logic', decision: 'logic', switch: 'logic', hours: 'logic', split: 'logic', set: 'logic', delay: 'logic', end: 'logic'
};
const GROUP_ORDER: Group[] = ['messages', 'ask', 'ai', 'people', 'logic', 'connect'];

export function NodePicker({ opened, onClose, onPick, onAssistant, hasTrigger, hidden }: {
  opened: boolean;
  onClose: () => void;
  onPick: (kind: string) => void;
  onAssistant: () => void;
  hasTrigger: boolean;
  /** Kinds that cannot be added right now, such as the SMS step while the platform has no SMS. */
  hidden?: ReadonlySet<string>;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [current, setCurrent] = useState<string>('send');
  useEffect(() => { if (opened) { setQuery(''); setCurrent(hasTrigger ? 'send' : TRIGGER_NODE_TYPE); } }, [opened, hasTrigger]);

  const kinds = useMemo(() => {
    const all = [...(hasTrigger ? [] : [TRIGGER_NODE_TYPE]), ...PALETTE_ORDER.filter((k) => !hidden?.has(k))];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((k) => `${title(k, t)} ${hint(k, t)} ${long(k, t)}`.toLowerCase().includes(q));
  }, [query, hasTrigger, hidden, t]);
  const grouped = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const k of kinds) {
      const g = k === TRIGGER_NODE_TYPE ? 'trigger' : GROUP_OF[k] ?? 'logic';
      map.set(g, [...(map.get(g) ?? []), k]);
    }
    return ['trigger', ...GROUP_ORDER].filter((g) => map.has(g)).map((g) => ({ group: g, kinds: map.get(g)! }));
  }, [kinds]);
  const shown = kinds.includes(current) ? current : kinds[0];
  const spec = shown && shown !== TRIGGER_NODE_TYPE ? NODE_KINDS[shown] : null;
  const ports = shown && shown !== TRIGGER_NODE_TYPE && spec ? outputsFor({ type: shown, data: spec.defaults }) : [];

  return (
    <Modal opened={opened} onClose={onClose} title={t('flows.builder.picker.title')} size="xl">
      <div className="rr-picker">
        <div className="rr-picker__list">
          <TextInput
            aria-label={t('flows.builder.picker.search')}
            placeholder={t('flows.builder.picker.search')}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            autoFocus
          />
          {grouped.length === 0 && <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.builder.picker.empty')}</Text>}
          {grouped.map(({ group, kinds: ks }) => (
            <div key={group} className="rr-picker__group">
              <div className="rr-picker__group-title">{t(`flows.builder.picker.groups.${group}`)}</div>
              {ks.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`rr-picker__item${k === shown ? ' is-current' : ''}`}
                  style={{ ['--rr-node-tint' as string]: tint(k) }}
                  onClick={() => setCurrent(k)}
                  onDoubleClick={() => { onPick(k); onClose(); }}
                >
                  <span className="rr-picker__icon">{icon(k)}</span>
                  <span className="rr-picker__text">
                    <span className="rr-picker__name">{title(k, t)}</span>
                    <span className="rr-picker__hint">{hint(k, t)}</span>
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
        {shown && (
          <div className="rr-picker__detail" style={{ ['--rr-node-tint' as string]: tint(shown) }}>
            <Group gap="sm" align="center">
              <span className="rr-picker__icon rr-picker__icon--big">{icon(shown)}</span>
              <Stack gap={0}>
                <Text role="bodySmall" fw={700}>{title(shown, t)}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)">{hint(shown, t)}</Text>
              </Stack>
            </Group>
            <Text role="bodySmall">{long(shown, t)}</Text>
            <div>
              <Text role="caption" fw={700}>{t('flows.builder.picker.useWhen')}</Text>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{when(shown, t)}</Text>
            </div>
            <div>
              <Text role="caption" fw={700}>{t('flows.builder.picker.outputs')}</Text>
              {ports.length > 0 ? (
                <ul className="rr-picker__ports">
                  {ports.map((p) => <li key={p.id}>{portLabel(p, t, shown)}</li>)}
                </ul>
              ) : (
                <Text role="bodySmall" c="var(--rr-color-text-muted)">
                  {shown === 'end' || shown === 'handoff' || shown === 'flow' ? t('flows.builder.picker.noOutputsEnds') : t('flows.builder.picker.oneOutput')}
                </Text>
              )}
            </div>
            <Group justify="space-between" align="center" wrap="wrap" className="rr-picker__actions">
              <Button size="xs" variant="subtle" onClick={() => { onClose(); onAssistant(); }}>{t('flows.builder.picker.assistantLink')}</Button>
              <Button size="sm" onClick={() => { onPick(shown); onClose(); }}>{t('flows.builder.picker.add', { name: title(shown, t) })}</Button>
            </Group>
          </div>
        )}
      </div>
    </Modal>
  );
}

function title(kind: string, t: Translate): string {
  return kind === TRIGGER_NODE_TYPE ? t('flows.builder.node.trigger') : t(`flows.builder.node.${kind}`);
}
function hint(kind: string, t: Translate): string {
  return kind === TRIGGER_NODE_TYPE ? t('flows.builder.node.triggerHint') : t(`flows.builder.node.${kind}Hint`);
}
function long(kind: string, t: Translate): string {
  return t(`flows.builder.picker.long.${kind === TRIGGER_NODE_TYPE ? 'trigger' : kind}`);
}
function when(kind: string, t: Translate): string {
  return t(`flows.builder.picker.when.${kind === TRIGGER_NODE_TYPE ? 'trigger' : kind}`);
}
function tint(kind: string): string {
  return kind === TRIGGER_NODE_TYPE ? 'var(--rr-color-success, #16a34a)' : NODE_KINDS[kind]?.tint ?? '#64748b';
}
function icon(kind: string) {
  const name = kind === TRIGGER_NODE_TYPE ? 'whatsapp' : NODE_KINDS[kind]?.icon ?? 'sparkles';
  return ICONS[name] ?? ICONS.sparkles;
}
